import React from "react";
import {
  Check,
  Clock,
  Shield,
  Calendar,
  Zap,
  Plus,
  Wallet,
  RefreshCw,
} from "lucide-react";

/**
 * ActivityFeed — right-rail event stream of recent PNR + system actions.
 *
 * Each item is a small icon badge (tone-colored 24px rounded square),
 * verb + train + descriptive text, and a mono footer line carrying the
 * PNR number and relative time.
 *
 * Props:
 *   items      — Activity[] (see shape below)
 *   loading    — bool; renders 4 skeleton rows
 *   onViewAll  — optional handler for the All link in the header
 *
 * Activity shape (Phase 1 mock — both old and Phase 2 backend field
 * names are accepted so the component does not need to change when the
 * real /api/activity endpoint lands):
 *   {
 *     id,
 *     type: confirm | delay | chart | sync | rac | add | wallet | refresh,
 *     pnr | pnr_number,
 *     train | train_name,
 *     text | message,
 *     time | created_at
 *   }
 */

const activityMeta = {
  confirm: { Icon: Check,     verb: "Confirmed", tone: "emerald" },
  delay:   { Icon: Clock,     verb: "Delay",     tone: "amber" },
  chart:   { Icon: Shield,    verb: "Chart",     tone: "indigo" },
  sync:    { Icon: Calendar,  verb: "Sync",      tone: "indigo" },
  rac:     { Icon: Zap,       verb: "RAC",       tone: "amber" },
  add:     { Icon: Plus,      verb: "Added",     tone: "zinc" },
  wallet:  { Icon: Wallet,    verb: "Wallet",    tone: "emerald" },
  refresh: { Icon: RefreshCw, verb: "System",    tone: "zinc" },
};

const toneClasses = {
  emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  indigo: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  sky: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  zinc: "bg-zinc-700/40 text-zinc-300 border-zinc-700",
};

// Lightweight relative-time formatter for created_at ISO strings.
// Phase 1 mock data already pre-formats time as "2m ago"; this helper
// only activates when the backend sends an ISO timestamp.
const relativeTime = (raw) => {
  if (!raw) return "";
  if (typeof raw === "string" && !raw.endsWith("Z") && !raw.includes("T")) {
    // Already a human string like "2m ago"
    return raw;
  }
  const ts = typeof raw === "number" ? raw : Date.parse(raw);
  if (Number.isNaN(ts)) return String(raw);
  const secs = Math.floor((Date.now() - ts) / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? "yesterday" : `${days}d ago`;
};

const ActivityFeed = ({ items = [], loading = false, onViewAll }) => {
  if (loading) {
    return (
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4">
        <div className="h-4 w-32 bg-zinc-800/60 rounded mb-4 animate-pulse" />
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-start gap-2.5 mb-3">
            <div className="w-6 h-6 rounded-md bg-zinc-800/60 animate-pulse shrink-0" />
            <div className="flex-1 space-y-1">
              <div className="h-3 w-32 bg-zinc-800/60 rounded animate-pulse" />
              <div className="h-2.5 w-44 bg-zinc-800/40 rounded animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4"
      data-testid="activity-feed"
    >
      <div className="flex items-center justify-between mb-3 gap-2">
        <h3 className="text-[13px] font-semibold text-zinc-100 flex items-center gap-2 whitespace-nowrap min-w-0 font-heading">
          Activity Feed
          <span className="px-1.5 py-0.5 rounded text-[10px] bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 shrink-0 font-mono">
            LIVE
          </span>
        </h3>
        {onViewAll && (
          <button
            type="button"
            onClick={onViewAll}
            className="text-[11px] text-zinc-500 hover:text-zinc-300 shrink-0 transition-colors"
          >
            All
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-[12px] text-zinc-500 py-6 text-center">
          No recent activity
        </div>
      ) : (
        <div className="space-y-2.5">
          {items.map((item) => {
            const meta = activityMeta[item.type] || activityMeta.refresh;
            const Icon = meta.Icon;
            const pnr = item.pnr || item.pnr_number || "—";
            const train = item.train || item.train_name || "";
            const text = item.text || item.message || "";
            const time = relativeTime(item.time || item.created_at);
            return (
              <div
                key={item.id}
                className="flex items-start gap-2.5 relative"
                data-testid={`activity-feed-item-${item.id}`}
              >
                <div
                  className={`mt-0.5 w-6 h-6 rounded-md border flex items-center justify-center shrink-0 ${
                    toneClasses[meta.tone] || toneClasses.zinc
                  }`}
                >
                  <Icon size={12} strokeWidth={meta.tone === "emerald" ? 2.4 : 2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 min-w-0">
                    <span className="text-[12px] text-zinc-100 font-medium shrink-0">
                      {meta.verb}
                    </span>
                    <span className="text-[11px] text-zinc-500 truncate">
                      · {train}
                    </span>
                  </div>
                  {text && (
                    <div className="text-[11.5px] text-zinc-400 mt-0.5 break-words">
                      {text}
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-1 text-[10px] text-zinc-600 whitespace-nowrap font-mono">
                    <span>{pnr}</span>
                    {time && (
                      <>
                        <span>·</span>
                        <span>{time}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ActivityFeed;
